import { Link, useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import { useTranslation } from "react-i18next"
import useSuppliers from "hooks/useSuppliers"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import PageTitle from "features/navigation/pageTitle"
import RightSidebarSupplier from "@/components/sidebar/right/RightSidebarSupplier"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import useStorages from "@/hooks/useStorages"
import { paramGuardStorage, paramGuardSupplier } from "@/utils/paramGuard"
import { AccessRole, DeviceSize, FormStatus } from "@/types"
import useLayout from "@/hooks/useLayout"
import useAccess from "@/hooks/useAccess"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import { useState } from "react"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"
import FloatingButtonGroup from "@/components/buttons/floatingButtonGroup"
import FloatingButtonGroupButton from "@/components/buttons/FloatingButtonGroupButton"
import FloatingButtonGroupHr from "@/components/buttons/FloatingButtonGroupHr"
import IconButton from "@/components/buttons/iconButton"
import { ReactComponent as MoreVertIcon } from "assets/icons/more_vert.svg"

const Supplier = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { displayIfSize } = useWindowDimensions()
    const { targetStorageDocId: storageId } = useTargetStorage()
    const { supplierId } = useParams<{ supplierId: string }>()

    const { 
        storage,
        storageStatus,
        isSuppliersEnabled
    } = useStorages({ storageId })
    
    const { 
        supplier, 
        supplierStatus,
        restoreSupplierAsync,
        isRestoring
    } = useSuppliers({ storageId, supplierId })

    const { addToast, openDialog } = useLayout()
    const { limitToRoles } = useAccess({ storageId: storageId || '' })
    const [isRestoringState, setIsRestoringState] = useState(false)

    const isLoading = supplierStatus === FormStatus.LOADING || !supplier?.supplierId;

    // Check params and declare safe ones to use in JSX
    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardSupplier({storageId, supplierId, supplier, supplierStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeSupplierId = supplierId!;

    // Redirect if suppliers are disabled for this storage
    if (!isSuppliersEnabled()) {
        navigate(`/storage`);
        return <></>;
    }

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreSupplierAsync({
                storageId: safeStorageId,
                supplierId: safeSupplierId
            })
            addToast({
                text: t('supplierRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    return (
        <>
            <PageTitle title={supplier?.name ? t('titleSupplier_', {name: supplier?.name, id: supplier.supplierId}) : t('titleSupplier')} />
            <MainSheetHeaderActions>
                {displayIfSize([DeviceSize.SM]) && supplier &&
                    <FloatingButtonGroup id={`supplier_edit_${safeSupplierId}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
                        <FloatingButtonGroupButton onClick={() => openDialog('supplier_history')}>{t('viewHistory')}</FloatingButtonGroupButton>
                        {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER]) && !supplier.isDeleted &&
                            <>
                                <FloatingButtonGroupButton>
                                    <Link to={`/storage/suppliers/${safeSupplierId}/edit`}>{t('edit')}</Link>
                                </FloatingButtonGroupButton>
                                <FloatingButtonGroupHr />
                                <FloatingButtonGroupButton onClick={() => openDialog('delete_supplier')}>{t('delete')}</FloatingButtonGroupButton>
                            </>
                        }
                    </FloatingButtonGroup>
                }
            </MainSheetHeaderActions>
            {supplier?.isDeleted && (
                <DeletedDocCallout
                    documentType="supplier"
                    deletionType="soft"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                />
            )}
            
            <Section>
                <TableList width="fit">
                    {!isLoading ?
                        <>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelSupplierEmail')}</b></TableListCell>
                                <TableListCell>{supplier.contactInformation?.email || '-'}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelSupplierPhone')}</b></TableListCell>
                                <TableListCell>{supplier.contactInformation?.phone || '-'}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelSupplierNotes')}</b></TableListCell>
                                <TableListCell>{supplier.notes || '-'}</TableListCell>
                            </TableListRow>
                        </> 
                        :
                        <>
                            {Array.from({ length: 3 }).map((_, _index) => (
                                <TableListRow key={_index} hoverEffect={false}>
                                    <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                    <TableListCell><p className="skeleton short"></p></TableListCell>
                                </TableListRow>
                            ))}
                            <TableListRow hoverEffect={false}>
                                <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                <TableListCell>
                                    <div className="grid gap-y-2">
                                        <p className="skeleton long"></p>
                                        <p className="skeleton medium"></p>
                                        <p className="skeleton short"></p>
                                    </div>
                                </TableListCell>
                            </TableListRow>
                        </>
                    }
                </TableList>
            </Section>

            <RightSidebarSupplier storageId={safeStorageId} supplierId={safeSupplierId} />
        </>
    )
}

export default Supplier
